import { useCallback, useMemo, useState } from "react";
import { Alert, Card, Select, Switch } from "antd";
import dayjs from "dayjs";

import { CalendarEventStatus } from "@fitness-app/data-models/entities/CalendarEvent";
import { cn } from "@fitness-app/utils/src/styles/cn";

import {
  getViewTitle,
  groupByDay,
  shiftViewDate,
  type CalendarView,
  type RenderEventOptions,
} from "~/components/EventCalendar/calendarModel";
import { CalendarToolbar } from "~/components/EventCalendar/CalendarToolbar";
import { CalendarViewSwitch } from "~/components/EventCalendar/CalendarViewSwitch";
import { StatusPill } from "~/components/EventCalendar/EventPopover";
import { useIsPhone } from "~/hooks/useIsMobile";
import { calendarEventStatusLabels } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/CalendarEventStatusBadge";
import { ConsultationChip } from "./ConsultationChip";
import { countByStatus, STATUS_ORDER, STATUS_PILL_TONES, type TrainerConsultation } from "./consultationModel";
import { useCalendarPosition } from "./useCalendarPosition";
import { useCalendarTrainer } from "./useCalendarTrainer";
import { useTrainerConsultations } from "./useTrainerConsultations";

const PHONE_VIEWS: CalendarView[] = ["agenda", "month"];
const DESKTOP_VIEWS: CalendarView[] = ["month", "week", "agenda"];

const getEventKey = (consultation: TrainerConsultation) => consultation.id;

/**
 * TRE-294 - the trainer's consultations across all of their clients, read-only. Today every row
 * comes from the Calendly webhook; working hours, leave and booking land on this same calendar
 * once consultations move off Calendly.
 */
const TrainerCalendar = () => {
  const isPhone = useIsPhone();
  const [showCanceled, setShowCanceled] = useState(false);
  const { view, dateKey, setView, setDate, goToToday } = useCalendarPosition();
  const { canPickTrainer, trainers, selectedTrainer, selectTrainer, trainerUserId } = useCalendarTrainer();

  const activeView = isPhone && view === "week" ? "month" : view;
  const viewDate = useMemo(() => dayjs(dateKey), [dateKey]);

  // Month and agenda only draw days of the month, the week only its seven days.
  const range = useMemo(
    () =>
      activeView === "week"
        ? { start: viewDate.startOf("isoWeek"), end: viewDate.endOf("isoWeek") }
        : { start: viewDate.startOf("month"), end: viewDate.endOf("month") },
    [viewDate, activeView],
  );

  const { data: consultations = [], isFetching, isError } = useTrainerConsultations(trainerUserId, range);

  const counts = useMemo(() => countByStatus(consultations), [consultations]);
  const eventsByDay = useMemo(
    () =>
      groupByDay(
        showCanceled
          ? consultations
          : consultations.filter((consultation) => consultation.status !== CalendarEventStatus.CANCELED),
        (consultation) => consultation.startTime,
      ),
    [consultations, showCanceled],
  );

  const renderEvent = useCallback(
    (consultation: TrainerConsultation, options: RenderEventOptions) => (
      <ConsultationChip consultation={consultation} {...options} />
    ),
    [],
  );

  return (
    <Card styles={{ body: { padding: isPhone ? 12 : 20 } }}>
      <div className="flex flex-col gap-4">
        <CalendarToolbar
          title={getViewTitle(activeView, viewDate)}
          view={activeView}
          views={isPhone ? PHONE_VIEWS : DESKTOP_VIEWS}
          onViewChange={setView}
          onPrev={() => setDate(shiftViewDate(activeView, viewDate.toDate(), -1))}
          onNext={() => setDate(shiftViewDate(activeView, viewDate.toDate(), 1))}
          onToday={goToToday}
          extra={
            canPickTrainer && (
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Wybierz trenera"
                className="w-full sm:w-64"
                value={selectedTrainer?.id}
                onChange={selectTrainer}
                options={trainers.map((trainer) => ({
                  value: trainer.id,
                  label: `${trainer.firstName} ${trainer.lastName}`,
                }))}
              />
            )
          }
        />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_ORDER.map((status) => (
              <StatusPill key={status} tone={STATUS_PILL_TONES[status]}>
                {calendarEventStatusLabels[status]}
                <span className="tabular-nums opacity-70">{counts[status]}</span>
              </StatusPill>
            ))}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <Switch size="small" checked={showCanceled} onChange={setShowCanceled} />
            Pokaż anulowane
          </label>
        </div>
        {isError && <Alert type="error" showIcon message="Nie udało się pobrać konsultacji" />}
        <div className={cn("transition-opacity", isFetching && "opacity-60")}>
          <CalendarViewSwitch
            view={activeView}
            date={viewDate}
            eventsByDay={eventsByDay}
            getEventKey={getEventKey}
            renderEvent={renderEvent}
          />
        </div>
      </div>
    </Card>
  );
};

export default TrainerCalendar;
